import { BulletinsService } from 'src/services/bulletins.service';
import { Checkbox } from 'src/components/ui/checkbox';
import { CreditAppService } from 'src/services/credit-app.service';
import { DocumentType } from 'src/utils/enums/documents.enum';
import { formatDateAndTime } from 'src/utils/formatDate';
import { formatMoney } from 'src/utils/formatMoney';
import Input from 'src/components/forms/input';
import { IUser } from 'src/utils/interfaces/user';
import { matchVINWithIOLetter } from 'src/utils/validators';
import { QuickFundMessageEnum } from 'src/utils/enums/quickFund.enum';
import React from 'react';
import Spinner from 'src/components/ui/spinner';
import Tooltip from 'src/components/ui/tooltip/index';
import { useLocalization } from 'src/utils/useLocalization';
import { useSelector } from 'react-redux';
import { countryMappper, numberOrDefault } from 'src/utils/helpers';
import {
    FinanceType,
    FinanceTypeNumericMap,
} from 'src/utils/enums/financialTypes.enum';
import {
    ICreditStatusObject,
    ISaveQuickFundRequest,
} from 'src/services/models/credit.service';
import Notification, {
    NotificationType,
} from 'src/components/ui/notification/Notification';

interface QuickFundNotificationProps {
    state: ICreditStatusObject;
    reasonMessage?: QuickFundMessageEnum;
}

interface QuickFundDetailsProps {
    state: ICreditStatusObject;
    onClose: () => void;
}

interface INotificationState {
    content: string;
    type: NotificationType;
    visible: boolean;
}

interface IInputConfig {
    type: string;
    name: string;
    value: string | number;
    handlerChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handlerBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    disabled?: boolean;
    prefix?: string;
    sufix?: string;
    min?: number;
    max?: number;
    maxlength?: number;
    customClass?: string;
    asErrors?: string;
    asTouched?: boolean;
}

interface IQuickFundDetailItem {
    label: string;
    value: string;
    isRequired?: boolean;
    tooltip?: string;
    inputConfig?: IInputConfig;
    error?: string;
}

const isLeaseContractType = (contractType: string): boolean => {
    const leaseContractTypes = ['LEASE'];
    return leaseContractTypes.includes(contractType.toUpperCase());
};

const RequiredFieldIndicator: React.FC = () => {
    return <span className='superscript-asterisk'>*</span>;
};

const QuickFundNotification: React.FC<QuickFundNotificationProps> = ({
    state,
    reasonMessage,
}) => {
    const { quickFund } = useLocalization();
    if (!state.quickfundData) {
        return null;
    }
    const getNotificationContent = () => {
        switch (reasonMessage) {
            case QuickFundMessageEnum.Learn:
                return quickFund.MESSAGES.LEARN;
            case QuickFundMessageEnum.NotEligibleSendToVCI:
                return quickFund.MESSAGES.NOT_ELIGIBLE_SEND_TO_VCI;
            case QuickFundMessageEnum.DoesNotQualifyMessage:
                return quickFund.MESSAGES.DOES_NOT_QUALIFY;
            case QuickFundMessageEnum.Option:
                return quickFund.MESSAGES.OPTION;
            case QuickFundMessageEnum.Due:
                return quickFund.MESSAGES.DUE;
            case QuickFundMessageEnum.TryAgainLater:
                return quickFund.MESSAGES.TRY_AGAIN_LATER;
            case QuickFundMessageEnum.EligibleMessage:
            case QuickFundMessageEnum.EligibleMessageWithContract:
                const isEligible = state.quickfundData?.isEligible;
                return !isEligible ? (
                    quickFund.MESSAGES.VIEW_ONLY_PERMISSION
                ) : (
                    <ul className='quickfund-notification-list'>
                        <li>{quickFund.NOTE1}</li>
                        <li>{quickFund.NOTE2}</li>
                        <li>{quickFund.NOTE3}</li>
                    </ul>
                );
            case QuickFundMessageEnum.NotEligibleDueToMissingOrFailedContractValidation:
                return quickFund.MESSAGES
                    .NOT_ELIGIBLE_DUE_TO_MISSING_OR_FAILED_CONTRACT;
            default:
                return null;
        }
    };

    const content = getNotificationContent();
    return content ? (
        <section
            id='js-notifications-group-mt9l92s5'
            className='c-notifications-group  js-panelgroup'
            aria-label='QuickFund notifications'
        >
            <div className='c-notification__container  js-notification-container'>
                <article
                    className='c-notification c-notification--info'
                    aria-labelledby='notification-title-mt9l92s5-0'
                >
                    <div className='c-notification__outer-wrap'>
                        <div className='c-notification__inner-wrap'>
                            <i
                                className='c-notification__status-icon'
                                aria-hidden='true'
                            ></i>
                            <div className='c-notification__content'>
                                <p className='c-notification__text'>
                                    {typeof content === 'string' ? (
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: content,
                                            }}
                                        />
                                    ) : (
                                        content
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>
                </article>
            </div>
        </section>
    ) : null;
};

const QuickFundDetailsItem: React.FC<IQuickFundDetailItem> = ({
    label,
    value,
    isRequired,
    tooltip,
    inputConfig,
    error,
}) => {
    return (
        <div className='quickfund-detail-item u-mt' key={label}>
            {inputConfig ? (
                <div>
                    <label className='c-form-field__label'>
                        {inputConfig.name}
                        {isRequired && <RequiredFieldIndicator />}
                        {tooltip && <Tooltip text={tooltip} />}
                    </label>
                    <Input
                        type={inputConfig.type}
                        name={inputConfig.name}
                        value={inputConfig.value}
                        handlerChange={inputConfig.handlerChange}
                        handlerBlur={inputConfig.handlerBlur}
                        disabled={inputConfig.disabled}
                        prefix={inputConfig.prefix}
                        sufix={inputConfig.sufix}
                        min={inputConfig.min}
                        max={inputConfig.max}
                        maxlength={inputConfig.maxlength}
                        asErrors={inputConfig.asErrors}
                        asTouched={inputConfig.asTouched}
                        customClass={`${
                            inputConfig.customClass
                        } quickfund-detail-item-input ${
                            isRequired ? 'quickfund-input-no-label' : ''
                        }`}
                    />
                </div>
            ) : (
                <div className='quickfund-detail-item-label'>
                    <h5>
                        {label}
                        {isRequired && <RequiredFieldIndicator />}
                        {tooltip && <Tooltip text={tooltip} />}
                    </h5>
                    <div className='quickfund-detail-item-value'>{value}</div>
                    {error && (
                        <div className='quickfund-detail-item-error'>
                            {error}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const QuickFundDetail: React.FC<{
    title: string;
    subtitle?: string;
    items: IQuickFundDetailItem[];
    status?: {
        label: string;
        type: 'success' | 'error' | 'warning' | 'info';
    };
}> = ({ title, subtitle, items, status }) => {
    return (
        <div className='quickfund-information--container u-mt'>
            <h4 className='quickfund-information--title'>
                {title}
                {status && (
                    <div
                        className={`quickfund-detail-item-status-container ${status.type}`}
                    >
                        {status.label}
                    </div>
                )}
            </h4>
            {subtitle && (
                <h5 className='quickfund-information--subtitle'>
                    {subtitle}
                    <RequiredFieldIndicator />
                </h5>
            )}
            <div className='quickfund-information--content'>
                {items.filter(Boolean).map((item) => (
                    <QuickFundDetailsItem
                        key={item.label}
                        label={item.label}
                        value={item.value}
                        isRequired={item.isRequired}
                        tooltip={item.tooltip}
                        inputConfig={item.inputConfig}
                        error={item.error}
                    />
                ))}
            </div>
        </div>
    );
};

const ApplicationInformation: React.FC<{
    state: ICreditStatusObject;
    reasonMessage?: QuickFundMessageEnum;
}> = ({ state, reasonMessage }) => {
    const { quickFund } = useLocalization();
    const quickFundData = state.quickfundData?.quickFund;
    const isEligible = state.quickfundData?.isEligible;
    const allowedReasons = [
        QuickFundMessageEnum.EligibleMessage,
        QuickFundMessageEnum.EligibleMessageWithContract,
    ];
    const isAllowedReason = allowedReasons.includes(
        reasonMessage || QuickFundMessageEnum.TryAgainLater,
    );

    if (!isAllowedReason || !isEligible) {
        return null;
    }

    const getApplicantName = () => {
        const businessName = state?.businessName || '';
        const firstName = state?.applicantFirstName || '';
        const lastName = state?.applicantLastName || '';

        return businessName || `${firstName} ${lastName}`;
    };

    const getVehicleDescription = () => {
        const vehicle = state?.creditApplication.Vehicle;
        if (!vehicle) {
            return '';
        }

        const { Year, Make, Model, BodyType } = vehicle;
        return `${Year || ''} ${Make || ''} ${Model || ''} ${BodyType || ''}`;
    };

    const getContractType = () => {
        return (
            state?.creditApplication.FinanceInformation.FinanceType ||
            quickFundData?.FinanceType ||
            ''
        );
    };

    const mapContractTypeToDisplay = (contractType: string) => {
        const contractTypeMap: Record<string, string> = {
            BALLOON: quickFund.APPLICATION_INFORMATION.BALLOON_CONTRACT,
        };

        return contractTypeMap[contractType.toUpperCase()] || contractType;
    };

    const contractType = getContractType();
    const isLease = isLeaseContractType(contractType);

    const getAmountToFinanceOrAdjustedCapCost = () => {
        if (isLease) {
            return (
                state?.creditApplication.FinanceInformation.AdjustedCapCost ||
                quickFundData?.AdjustedCapCost
            );
        } else {
            return (
                state?.creditApplication.FinanceInformation.LoanAmount ||
                quickFundData?.AmountToFinance
            );
        }
    };

    const items: IQuickFundDetailItem[] = [
        {
            label: quickFund.APPLICATION_INFORMATION.PRIMARY_APPLICANT,
            value: getApplicantName(),
        },
        {
            label: quickFund.APPLICATION_INFORMATION.VEHICLE,
            value: getVehicleDescription(),
        },
        {
            label: isLease
                ? quickFund.APPLICATION_INFORMATION.ADJUSTED_CAP_COST
                : quickFund.APPLICATION_INFORMATION.AMOUNT_TO_FINANCE,
            value: formatMoney(getAmountToFinanceOrAdjustedCapCost()),
        },
        {
            label: quickFund.APPLICATION_INFORMATION.CONTRACT_TYPE,
            value: mapContractTypeToDisplay(contractType),
        },
    ];

    return (
        <QuickFundDetail
            title={quickFund.APPLICATION_INFORMATION.TITLE}
            items={items}
        />
    );
};

const QuickFundInformation: React.FC<{
    state: ICreditStatusObject;
    onClose: () => void;
    reasonMessage?: QuickFundMessageEnum;
    setReasonMessage: (reason: QuickFundMessageEnum) => void;
    showNotification: (content: string, type: NotificationType) => void;
    user: {
        firstName: string;
        lastName: string;
        fullName: string;
        dealerId: string;
        country: string;
        partyId: number | string;
    };
}> = ({
    state,
    onClose,
    reasonMessage,
    setReasonMessage,
    showNotification,
    user: { firstName, lastName, dealerId, country, partyId },
}) => {
    const ZERO_AMOUNT = 0;
    const [isLoading, setIsLoading] = React.useState(false);
    const submitBtnRef = React.useRef<HTMLButtonElement>(null);
    const handleTermsClick = async (e: React.MouseEvent) => {
        const target = e.target as HTMLElement;
        if (target.classList.contains('terms-and-conditions')) {
            e.preventDefault();
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
            const newTab = window.open('', '_blank');
            if (!newTab) return false;
            try {
                const service = new BulletinsService();
                const brand = 'vw';
                const res = await service.getDocuments(
                    brand,
                    'USA',
                    DocumentType.TERMS_AND_CONDITIONS,
                    'en',
                );
                const contentType = String(res.headers?.['content-type'] || '');
                if (contentType.includes('application/json')) {
                    const text =
                        res.data instanceof Blob
                            ? await res.data.text()
                            : JSON.stringify(res.data);
                    const parsed = JSON.parse(text);
                    newTab.location.assign(parsed.downloadUrl);
                } else {
                    const pdfUrl = URL.createObjectURL(res.data);
                    newTab.location.assign(pdfUrl);
                }
                return true;
            } catch (e) {
                newTab.document.body.innerHTML =
                    '<h1>Failed to load PDF</h1><p>Please try again later.</p>';
                return false;
            }
        }
    };
    const [submitBtnWidth, setSubmitBtnWidth] = React.useState<
        number | undefined
    >(undefined);
    const { quickFund, labelField } = useLocalization();
    const quickFundData = state.quickfundData?.quickFund;
    const isEligible = state.quickfundData?.isEligible;
    const allowedReasons = [
        QuickFundMessageEnum.EligibleMessage,
        QuickFundMessageEnum.EligibleMessageWithContract,
        QuickFundMessageEnum.AlreadyQuickFunded,
        QuickFundMessageEnum.RecentlySubmitted,
    ];
    const isAllowedReason = allowedReasons.includes(
        reasonMessage || QuickFundMessageEnum.TryAgainLater,
    );
    const canSubmit =
        reasonMessage !== QuickFundMessageEnum.AlreadyQuickFunded &&
        reasonMessage !== QuickFundMessageEnum.RecentlySubmitted;
    const hasValidatedContract =
        reasonMessage === QuickFundMessageEnum.EligibleMessageWithContract;

    if (!isAllowedReason || (canSubmit && !isEligible)) {
        return null;
    }

    const [isAgreed, setIsAgreed] = React.useState(false);
    const isLease = isLeaseContractType(
        state?.creditApplication.FinanceInformation.FinanceType,
    );
    const [otherDeductionAmount, setOtherDeductionAmount] =
        React.useState<string>('');
    const [otherDeductionError, setOtherDeductionError] =
        React.useState<string>('');
    const otherDeduction = Number(otherDeductionAmount) || ZERO_AMOUNT;

    const getSubtitle = () => {
        return canSubmit ? quickFund.INFORMATION.REQUIRED : '';
    };

    const [adjustedCapCost, setAdjustedCapCost] = React.useState<string>(
        quickFundData?.AdjustedCapCost
            ? String(quickFundData.AdjustedCapCost)
            : '',
    );
    const [adjustedCapCostError, setAdjustedCapCostError] =
        React.useState<string>('');
    const amountToFinance = quickFundData?.AmountToFinance || ZERO_AMOUNT;
    const [securityDeposit, setSecurityDeposit] = React.useState<string>(
        quickFundData?.SecurityDeposit
            ? String(quickFundData.SecurityDeposit)
            : '',
    );
    const [securityDepositError, setSecurityDepositError] =
        React.useState<string>('');
    const [monthlyPayment, setMonthlyPayment] = React.useState<string>(
        quickFundData?.MonthlyPayment
            ? String(quickFundData.MonthlyPayment)
            : '',
    );
    const [monthlyPaymentError, setMonthlyPaymentError] =
        React.useState<string>('');
    const [acquisitionFee, setAcquisitionFee] = React.useState<string>(
        quickFundData?.AcquisitionFee
            ? String(quickFundData.AcquisitionFee)
            : '',
    );
    const [acquisitionFeeError, setAcquisitionFeeError] =
        React.useState<string>('');
    const [vin, setVin] = React.useState<string>(quickFundData?.Vin || '');
    const [vinError, setVinError] = React.useState<string>('');
    const MAX_OTHER_DEDUCTION = 9999.99;
    const MAX_AMOUNT = 999999.99;
    const MAX_ACQUISITION_FEE = 895.0;
    const getMaxAmountFinanced = () => {
        if (isLease) {
            return MAX_AMOUNT;
        }
        const amountToFinance =
            Number(state?.creditApplication.FinanceInformation.LoanAmount) ||
            ZERO_AMOUNT;
        const maxMultiplier = 1.5;
        return amountToFinance * maxMultiplier || MAX_AMOUNT;
    };

    const [editableAdvanceAmount, setEditableAdvanceAmount] =
        React.useState<string>('');
    const getAdvanceAmount = () => {
        if (isLease)
            return (
                Number(adjustedCapCost) -
                (Number(securityDeposit) +
                    Number(monthlyPayment) +
                    Number(acquisitionFee) +
                    otherDeduction)
            );
        else if (!isLease && !hasValidatedContract)
            return Number(editableAdvanceAmount);
        else return Number(amountToFinance) - otherDeduction;
    };
    const advanceAmount = getAdvanceAmount();
    const [advanceAmountError, setAdvanceAmountError] =
        React.useState<string>('');

    const validateMaxAmount = (
        value: string,
        max: number,
        errorMessage: string,
        setError: (error: string) => void,
    ) => {
        const num = Number(value) || ZERO_AMOUNT;
        if (num > max) {
            setError(errorMessage);
        } else {
            setError('');
        }
    };

    const validateRequiredLoanRetailFields = () => {
        let isValid = true;
        if (!vin) {
            setVinError(labelField.LABEL_FIELD_ERROR_REQUIRED);
            isValid = false;
        }
        if (!editableAdvanceAmount) {
            setAdvanceAmountError(labelField.LABEL_FIELD_ERROR_REQUIRED);
            isValid = false;
        }

        return isValid;
    };

    const validateRequiredLeaseFields = () => {
        let isValid = true;
        if (!vin) {
            setVinError(labelField.LABEL_FIELD_ERROR_REQUIRED);
            isValid = false;
        }
        if (!adjustedCapCost) {
            setAdjustedCapCostError(labelField.LABEL_FIELD_ERROR_REQUIRED);
            isValid = false;
        }
        if (!monthlyPayment) {
            setMonthlyPaymentError(labelField.LABEL_FIELD_ERROR_REQUIRED);
            isValid = false;
        }
        if (!acquisitionFee) {
            setAcquisitionFeeError(labelField.LABEL_FIELD_ERROR_REQUIRED);
            isValid = false;
        }

        return isValid;
    };

    const validateRequiredFields = () => {
        if (isLease && !hasValidatedContract) {
            return validateRequiredLeaseFields();
        }
        if (!isLease && !hasValidatedContract) {
            return validateRequiredLoanRetailFields();
        }
        return true;
    };

    const validateAdvanceAmount = () => {
        if (advanceAmount <= ZERO_AMOUNT) {
            setAdvanceAmountError(quickFund.INFORMATION.ADVANCE_AMOUNT_ERROR);
            return false;
        }
        setAdvanceAmountError('');
        return true;
    };

    const submitHandler = async () => {
        setIsLoading(true);
        setSubmitBtnWidth(submitBtnRef.current!.offsetWidth);

        const areAllFieldsValid = validateRequiredFields();
        const isAdvanceAmountValid = validateAdvanceAmount();
        if (!areAllFieldsValid || !isAdvanceAmountValid) {
            setIsLoading(false);
            return;
        }

        const setQuickfundAmountField = (
            amount: string | number,
            shoudSend: boolean,
        ) => {
            return shoudSend ? Number(amount) : ZERO_AMOUNT;
        };

        try {
            const creditAppService = new CreditAppService();
            const saveQuickFundRequest: ISaveQuickFundRequest = {
                dealerId: dealerId,
                country: country,
                partyId: partyId,
                firstName: firstName,
                lastName: lastName,
                quickFund: {
                    AccountNumber: state.applicationNumber.toString(),
                    Vin: vin,
                    AcquisitionFee: setQuickfundAmountField(
                        acquisitionFee,
                        isLease,
                    ),
                    AdjustedCapCost: setQuickfundAmountField(
                        adjustedCapCost,
                        isLease,
                    ),
                    AdvanceAmount: setQuickfundAmountField(advanceAmount, true),
                    SecurityDeposit: setQuickfundAmountField(
                        securityDeposit,
                        isLease,
                    ),
                    MonthlyPayment: setQuickfundAmountField(
                        monthlyPayment,
                        isLease,
                    ),
                    AmountToFinance: setQuickfundAmountField(
                        amountToFinance,
                        !isLease && hasValidatedContract,
                    ),
                    FinanceType:
                        FinanceTypeNumericMap[
                            (state.creditApplication.FinanceInformation
                                .FinanceType as FinanceType) || FinanceType.None
                        ],
                    // SubmittedBy: state.submittedBy,
                    SubmittedDate: new Date().toISOString(),
                },
            };
            await creditAppService.submitQuickFund(saveQuickFundRequest);
            showNotification(
                quickFund.INFORMATION.SUCCESS_NOTIFICATION,
                NotificationType.SUCCESS,
            );
            setIsLoading(false);
            setReasonMessage(QuickFundMessageEnum.RecentlySubmitted);
        } catch (error) {
            showNotification(
                quickFund.INFORMATION.ERROR_NOTIFICATION,
                NotificationType.ERROR,
            );
            setIsLoading(false);
        }
    };

    const SubmitContainer: React.FC = () => {
        return (
            (canSubmit && (
                <div className='quickfund-submit-container u-mt-large'>
                    <div
                        className='quickfund-submit-agreement'
                        onClickCapture={handleTermsClick}
                    >
                        <Checkbox
                            name=''
                            value='agreement'
                            checked={isAgreed}
                            onCheck={(checked) => setIsAgreed(checked)}
                            html={quickFund.INFORMATION.AGREEMENT}
                        />
                    </div>
                    <div className='quickfund-note u-mt-large'>
                        <span
                            dangerouslySetInnerHTML={{
                                __html: quickFund.INFORMATION.NOTE,
                            }}
                        ></span>
                    </div>
                    <div className='quickfund-buttons-container u-mt'>
                        <button
                            className='c-btn c-btn--secondary c-btn--small'
                            aria-label='Close'
                            onClick={onClose}
                        >
                            <span className='c-btn__text'>
                                {quickFund.INFORMATION.CANCEL_BTN}
                            </span>
                        </button>
                        <button
                            ref={submitBtnRef}
                            className='c-btn c-btn--small'
                            aria-label='Submit'
                            onClick={submitHandler}
                            disabled={
                                !isAgreed ||
                                isLoading ||
                                !!otherDeductionError ||
                                !!adjustedCapCostError ||
                                !!securityDepositError ||
                                !!monthlyPaymentError ||
                                !!acquisitionFeeError ||
                                !!vinError ||
                                !!advanceAmountError
                            }
                            style={
                                submitBtnWidth
                                    ? { minWidth: submitBtnWidth }
                                    : undefined
                            }
                        >
                            {isLoading ? (
                                <Spinner />
                            ) : (
                                <span className='c-btn__text'>
                                    {quickFund.INFORMATION.SUBMIT_BTN}
                                </span>
                            )}
                        </button>
                    </div>
                </div>
            )) ||
            null
        );
    };

    const handleChangeDecimal = (
        e: React.ChangeEvent<HTMLInputElement>,
        setField: (value: string) => void,
    ) => {
        // if (e.target.name !== FIELD) return handleInputChange(e);
        let value = e.target.value;
        value = value.replace(/[^\d.]/g, '');
        const parts = value.split('.');

        const int = parts[0] || '';
        const dec = parts[1] || '';

        const formatted =
            // eslint-disable-next-line no-magic-numbers
            int + (value.includes('.') ? '.' + dec.slice(0, 2) : '');
        const num = Number(formatted);
        // eslint-disable-next-line no-magic-numbers
        if (!isNaN(num) && num > 999999.99) return;

        e.target.value = formatted;
        setField(formatted);
    };

    const setItemLabelOrInput = (
        label: string,
        value: string | undefined,
        shouldDisplay: boolean,
        isRequired?: boolean,
        isInput?: boolean,
        inputConfig?: IInputConfig,
        tooltip?: string,
        error?: string,
    ): IQuickFundDetailItem => {
        if (!shouldDisplay) {
            return undefined as unknown as IQuickFundDetailItem;
        }

        return {
            label,
            value: isInput || !value ? '' : value,
            isRequired,
            inputConfig: isInput ? inputConfig : undefined,
            tooltip,
            error,
        };
    };

    const inputClasss = 'quickfund-other-amount-input';

    const items: IQuickFundDetailItem[] = [
        setItemLabelOrInput(
            quickFund.STATUS.BULLETIN_NUMBER,
            quickFundData?.BulletinNumber,
            !!quickFundData?.BulletinNumber && canSubmit,
        ),
        setItemLabelOrInput(
            quickFund.INFORMATION.VIN,
            vin,
            true,
            canSubmit,
            !hasValidatedContract && canSubmit,
            {
                type: 'text',
                name: quickFund.INFORMATION.VIN,
                value: vin,
                handlerChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    setVin(e.target.value.toUpperCase());
                },
                handlerBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                    if (!e.target.value) {
                        setVinError(quickFund.INFORMATION.VIN_ERROR);
                    } else if (!matchVINWithIOLetter(e.target.value)) {
                        setVinError(quickFund.INFORMATION.VIN_ERROR);
                    } else {
                        setVinError('');
                    }
                },
                customClass: inputClasss,
                asErrors: vinError,
                asTouched: !!vinError,
                maxlength: 17,
            },
        ),
        setItemLabelOrInput(
            `${
                hasValidatedContract ? quickFund.INFORMATION.CONTRACT + ' ' : ''
            }${quickFund.APPLICATION_INFORMATION.ADJUSTED_CAP_COST}`,
            formatMoney(adjustedCapCost),
            isLease,
            canSubmit,
            !hasValidatedContract && canSubmit,
            {
                type: 'text',
                name: quickFund.APPLICATION_INFORMATION.ADJUSTED_CAP_COST,
                value: adjustedCapCost,
                prefix: '$',
                handlerChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    handleChangeDecimal(e, setAdjustedCapCost);
                    validateMaxAmount(
                        e.target.value,
                        MAX_AMOUNT,
                        quickFund.INFORMATION.ADJUSTED_CAP_COST_ERROR,
                        setAdjustedCapCostError,
                    );
                },
                min: 0,
                customClass: inputClasss,
                asErrors: adjustedCapCostError,
                asTouched: !!adjustedCapCostError,
            },
        ),
        setItemLabelOrInput(
            quickFund.INFORMATION.AMOUNT_FINANCED,
            formatMoney(amountToFinance),
            !isLease && hasValidatedContract,
            false,
            false,
            undefined,
            quickFund.INFORMATION.AMOUNT_FINANCED_TOOLTIP,
        ),
        setItemLabelOrInput(
            quickFund.INFORMATION.SECURITY_DEPOSIT,
            formatMoney(securityDeposit),
            isLease &&
                (quickFundData?.SecurityDeposit != null ||
                    !hasValidatedContract),
            false,
            !hasValidatedContract && canSubmit,
            {
                type: 'text',
                name: quickFund.INFORMATION.SECURITY_DEPOSIT,
                value: securityDeposit,
                prefix: '$',
                handlerChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    handleChangeDecimal(e, setSecurityDeposit);
                    validateMaxAmount(
                        e.target.value,
                        MAX_AMOUNT,
                        quickFund.INFORMATION.SECURITY_DEPOSIT_ERROR,
                        setSecurityDepositError,
                    );
                },
                min: 0,
                customClass: inputClasss,
                asErrors: securityDepositError,
                asTouched: !!securityDepositError,
            },
        ),
        setItemLabelOrInput(
            quickFund.INFORMATION.MONTHLY_PAYMENT,
            formatMoney(monthlyPayment),
            isLease &&
                (quickFundData?.MonthlyPayment != null ||
                    !hasValidatedContract),
            canSubmit,
            !hasValidatedContract && canSubmit,
            {
                type: 'text',
                name: quickFund.INFORMATION.MONTHLY_PAYMENT,
                value: monthlyPayment,
                prefix: '$',
                handlerChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    handleChangeDecimal(e, setMonthlyPayment);
                    validateMaxAmount(
                        e.target.value,
                        MAX_AMOUNT,
                        quickFund.INFORMATION.MONTHLY_PAYMENT_ERROR,
                        setMonthlyPaymentError,
                    );
                },
                min: 0,
                customClass: inputClasss,
                asErrors: monthlyPaymentError,
                asTouched: !!monthlyPaymentError,
            },
        ),
        setItemLabelOrInput(
            quickFund.INFORMATION.ACQUISITION_FEE,
            formatMoney(acquisitionFee),
            isLease,
            canSubmit,
            !hasValidatedContract && canSubmit,
            {
                type: 'text',
                name: quickFund.INFORMATION.ACQUISITION_FEE,
                value: acquisitionFee,
                prefix: '$',
                handlerChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    handleChangeDecimal(e, setAcquisitionFee);
                    validateMaxAmount(
                        e.target.value,
                        MAX_ACQUISITION_FEE,
                        quickFund.INFORMATION.ACQUISITION_FEE_ERROR,
                        setAcquisitionFeeError,
                    );
                },
                min: 0,
                customClass: inputClasss,
                asErrors: acquisitionFeeError,
                asTouched: !!acquisitionFeeError,
            },
        ),
        setItemLabelOrInput(
            quickFund.INFORMATION.OTHER_AMOUNT,
            '',
            QuickFundMessageEnum.EligibleMessageWithContract === reasonMessage,
            false,
            true,
            {
                type: 'text',
                name: quickFund.INFORMATION.OTHER_AMOUNT,
                value: otherDeductionAmount,
                prefix: '$',
                handlerChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    handleChangeDecimal(e, setOtherDeductionAmount);
                    validateMaxAmount(
                        e.target.value,
                        MAX_OTHER_DEDUCTION,
                        quickFund.INFORMATION.OTHER_AMOUNT_ERROR,
                        setOtherDeductionError,
                    );
                },
                min: 0,
                customClass: inputClasss,
                asErrors: otherDeductionError,
                asTouched: !!otherDeductionError,
            },
        ),
        setItemLabelOrInput(
            quickFund.INFORMATION.QUICKFUND_ADVANCE_AMOUNT,
            reasonMessage === QuickFundMessageEnum.AlreadyQuickFunded
                ? formatMoney(quickFundData?.AdvanceAmount || ZERO_AMOUNT)
                : formatMoney(advanceAmount),
            true,
            canSubmit,
            !isLease && !hasValidatedContract && canSubmit,
            {
                type: 'text',
                name: quickFund.INFORMATION.QUICKFUND_ADVANCE_AMOUNT,
                value: editableAdvanceAmount,
                prefix: '$',
                handlerChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    handleChangeDecimal(e, setEditableAdvanceAmount);
                    validateMaxAmount(
                        e.target.value,
                        getMaxAmountFinanced(),
                        quickFund.INFORMATION.ADVANCE_AMOUNT_ERROR,
                        setAdvanceAmountError,
                    );
                },
                min: 0,
                customClass: inputClasss,
                asErrors: advanceAmountError,
                asTouched: !!advanceAmountError,
            },
            !isLease
                ? quickFund.INFORMATION.QUICKFUND_ADVANCE_AMOUNT_BALLOON_TOOLTIP
                : quickFund.INFORMATION.QUICKFUND_ADVANCE_AMOUNT_TOOLTIP,
            advanceAmountError,
        ),
    ];

    return (
        <>
            <QuickFundDetail
                title={quickFund.INFORMATION.TITLE}
                items={items}
                subtitle={getSubtitle()}
            />
            <SubmitContainer />
        </>
    );
};

const QuickFundStatus: React.FC<{
    state: ICreditStatusObject;
    onClose: () => void;
    reasonMessage?: QuickFundMessageEnum;
    user: {
        fullName: string;
    };
}> = ({ state, onClose, reasonMessage, user }) => {
    const { quickFund, payments } = useLocalization();
    const quickFundData = state.quickfundData?.quickFund;
    const allowedReasons = [
        QuickFundMessageEnum.AlreadyQuickFunded,
        QuickFundMessageEnum.RecentlySubmitted,
    ];
    const isAllowedReason = allowedReasons.includes(
        reasonMessage || QuickFundMessageEnum.TryAgainLater,
    );
    const isRecentlySubmitted =
        reasonMessage === QuickFundMessageEnum.RecentlySubmitted;
    const submittedDate = isRecentlySubmitted
        ? formatDateAndTime(new Date(new Date().toISOString()).toString())
        : formatDateAndTime(quickFundData?.SubmittedDate || '');
    const submittedBy = isRecentlySubmitted
        ? user.fullName
        : quickFundData?.SubmittedBy || '';
    if (!isAllowedReason) {
        return null;
    }

    const status: {
        label: string;
        type: 'success';
    } = { label: quickFund.STATUS.SUBMITTED, type: 'success' };

    const items: IQuickFundDetailItem[] = [
        ...(quickFundData?.BulletinNumber
            ? [
                  {
                      label: quickFund.STATUS.BULLETIN_NUMBER,
                      value: quickFundData.BulletinNumber,
                  },
              ]
            : []),
        ...(quickFundData?.ContractValidationNumber
            ? [
                  {
                      label: quickFund.STATUS.CONTRACT_VALIDATION_NUMBER,
                      value: quickFundData.ContractValidationNumber,
                  },
              ]
            : []),
        {
            label: quickFund.STATUS.SUBMITTED,
            value: submittedDate,
        },
        {
            label: quickFund.STATUS.SUBMITTED_BY,
            value: submittedBy,
        },
    ];
    return (
        <>
            <QuickFundDetail
                title={quickFund.STATUS.TITLE}
                items={items}
                status={status}
            />
            <div className='quickfund-buttons-container u-mt'>
                <button
                    className='c-btn c-btn--small'
                    aria-label='Close'
                    onClick={onClose}
                >
                    <span className='c-btn__text'>
                        {payments.PAYMENT_INFORMATION_DETAILS_BUTTONS_CLOSE}
                    </span>
                </button>
            </div>
        </>
    );
};

const QuickFundDetails: React.FC<QuickFundDetailsProps> = ({
    state,
    onClose,
}) => {
    const user = useSelector((state: IUser) => {
        const defaultDealer = state.currentDealers?.default ?? '';
        const [first, last] = (state.fullName || '').split(' ');
        return {
            dealerId: defaultDealer,
            country: countryMappper(state.dealerCountry ?? ''),
            partyId: state.partyId ? numberOrDefault(state.partyId) : '',
            firstName: first,
            lastName: last ?? '',
            fullName: state.fullName ?? '',
        };
    });
    const { quickFund } = useLocalization();
    const [reasonMessage, setReasonMessage] = React.useState(
        state.quickfundData?.reasonMessage,
    );
    const [notification, setNotification] = React.useState<INotificationState>({
        content: '',
        type: NotificationType.SUCCESS,
        visible: false,
    });

    const showNotification = (content: string, type: NotificationType) => {
        setNotification({ content, type, visible: true });
    };

    return (
        <div className='quickfund-details--container u-mt'>
            {notification.visible && (
                <Notification
                    content={notification.content}
                    type={notification.type}
                    visibility={notification.visible}
                    setVisibility={(value) => {
                        const visible =
                            typeof value === 'function'
                                ? value(notification.visible)
                                : value;
                        setNotification((prev) => ({ ...prev, visible }));
                    }}
                />
            )}
            <h4>{quickFund.DETAILS_TITLE}</h4>
            <QuickFundNotification
                state={state}
                reasonMessage={reasonMessage}
            />
            <ApplicationInformation
                state={state}
                reasonMessage={reasonMessage}
            />
            <QuickFundInformation
                state={state}
                onClose={onClose}
                reasonMessage={reasonMessage}
                setReasonMessage={setReasonMessage}
                showNotification={showNotification}
                user={user}
            />
            <QuickFundStatus
                state={state}
                onClose={onClose}
                reasonMessage={reasonMessage}
                user={{ fullName: user.fullName }}
            />
        </div>
    );
};

export default QuickFundDetails;
